import { useRef, useState } from "react";
import { MAX_PROMPT_LENGTH } from "@hatch/shared";
import { DEFAULT_FRAMEWORK, FRAMEWORK_OPTIONS, FRAMEWORK_PICKER_VISIBLE, type Framework } from "../../lib/frameworks.js";
import { PROMPT_GALLERY } from "../../lib/promptGallery.js";
import { startBuild } from "../../lib/useSessionStream.js";
import { XanoLogo, XanoScreen } from "./XanoScreen.js";

/**
 * One prompt box, and a short shelf of prompts to start from.
 *
 * Same promise as the other two landing screens and the same behaviour behind
 * it: no options, no configuration, and a gallery that fills the box with
 * editable text rather than selecting a template. Only the clothes differ.
 *
 * Two things here are departures rather than restyling, and both are the reason
 * this skin exists:
 *
 *   The box does NOT autofocus. Both other skins do, and on a desktop that is
 *   right — the cursor lands where the work is. This skin is discovered by
 *   scanning a QR code on a booth wall, and the first thing that happens then is
 *   that the on-screen keyboard covers the sentence explaining what you are
 *   looking at. The box is one tap away; the headline is the thing that has to
 *   land first.
 *
 *   The box's type is 16px and no smaller. Below that, iOS zooms the viewport on
 *   focus and does not zoom back, which leaves the visitor on a page they now
 *   have to pinch to read. It is the one place in the skin where a type size is
 *   a functional requirement rather than a design choice.
 */
export function PromptView({ onStarted }: { onStarted: (sessionId: string) => void }) {
  const [prompt, setPrompt] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [framework, setFramework] = useState<Framework>(DEFAULT_FRAMEWORK);
  const boxRef = useRef<HTMLTextAreaElement>(null);

  const trimmed = prompt.trim();
  const canSubmit = trimmed.length > 0 && !submitting;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);
    const result = await startBuild(trimmed, framework);
    if ("error" in result) {
      setError(result.error);
      setSubmitting(false);
      return;
    }
    onStarted(result.sessionId);
  }

  /** Fill the box and hand the user the cursor — a start, not a commitment. */
  function choose(text: string) {
    setPrompt(text);
    const box = boxRef.current;
    if (!box) return;
    box.focus();
    box.setSelectionRange(text.length, text.length);
  }

  const starters = resolveStarters();

  return (
    <XanoScreen>
      <header className="flex items-center justify-between px-5 py-4 sm:px-8 sm:py-5">
        <XanoLogo className="h-[18px]" />
      </header>

      <main className="mx-auto flex w-full max-w-[720px] flex-1 flex-col items-center px-5 pt-6 pb-16 sm:px-6 sm:pt-12">
        {/*
          Why this page exists and where to find the people who made it. First
          thing under the logo because a visitor arriving from the booth wall
          should see the booth acknowledged before anything else — and because
          someone who forwards the link to a colleague sends the invitation with
          it.
        */}
        <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-full border border-deck-blue/35 bg-deck-blue-soft px-3.5 py-1.5 text-center font-deck-mono text-[10.5px] leading-none font-medium tracking-[0.12em] text-deck-blue-hi uppercase">
          <span>AI TechWorld 2026</span>
          <span aria-hidden className="text-deck-text-faint">&middot;</span>
          <span>Booth 606</span>
        </p>

        <p className="mt-4 text-center font-deck-mono text-[11px] leading-[1.6] font-medium tracking-[0.18em] text-deck-text-faint uppercase">
          A live full-stack build, from one prompt
        </p>

        {/*
          The break after "Describe it." is forced rather than left to
          `text-balance`. Balancing alone put "Describe it. Get" on one line and
          carried the second sentence's opening verb up with the first
          sentence's full stop, which reads as a stumble at the exact size the
          eye lands on first. The two sentences are a call and its answer, and
          the line break is where that turn happens.

          "inspect" is the accented word, and it is set at display size — which
          is what lets it be full-strength $primary at 3.91:1. Anything read at
          body size uses the lighter blue. See the token note in index.css.
        */}
        <h1 className="mt-4 text-center text-[clamp(30px,8vw,50px)] leading-[1.08] font-bold tracking-[-0.02em] text-balance">
          <span className="block">Describe it.</span>
          Get software you can <span className="text-deck-blue">inspect</span>.
        </h1>

        <p className="mt-4 max-w-[520px] text-center text-[15.5px] leading-[1.6] text-pretty text-deck-text-soft sm:text-[17px]">
          Xano builds the database, the API, and the front end from one sentence &mdash; then
          deploys it. Every table and endpoint is yours to read afterwards.
        </p>

        <form onSubmit={submit} className="mt-8 w-full">
          {/*
            The bracketed box from the design. The corners are four elements
            rather than a border, because the design's frame is open at the
            edges — a border would close it, and closing it turns a viewfinder
            into a text field.
          */}
          <div className="relative">
            <Brackets />
            <div className="rounded-xl border border-deck-line bg-deck-raise/80 p-3 backdrop-blur-sm transition-colors focus-within:border-deck-blue sm:p-4">
              <label htmlFor="prompt" className="sr-only">
                Describe the website you want
              </label>
              <textarea
                id="prompt"
                ref={boxRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value.slice(0, MAX_PROMPT_LENGTH))}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") void submit(e);
                }}
                rows={3}
                disabled={submitting}
                // The same invitation the other two skins open with. A
                // worked example here instructs where this should invite, and
                // the starter chips below already demonstrate the detail that
                // works — by putting it in the box.
                placeholder="A site for…"
                // 16px is a floor, not a preference — see the note on this
                // component. `text-base` is exactly that in Tailwind's default
                // scale, so it is written as the utility rather than as a
                // number someone could later shave to 15.
                className="w-full resize-none bg-transparent px-2 py-1.5 text-base leading-[1.55] outline-none placeholder:text-deck-text-faint disabled:opacity-60 sm:min-h-[104px]"
              />

              <div className="mt-2 flex items-center justify-between gap-3 px-1">
                <div className="flex items-center gap-2.5">
                  {/*
                    The framework choice in this skin's hand: a segmented pair
                    rather than a select, so both options are visible without a
                    tap.

                    Not shown at present — FRAMEWORK_PICKER_VISIBLE in
                    lib/frameworks.ts is false while a Svelte build cannot
                    finish, so the form sends DEFAULT_FRAMEWORK and this row
                    holds only the counter and the submit. The markup stays for
                    the day that flips back.

                    Every button here is 44px tall. That is chunkier than the
                    design draws it and it is the trade the phone-first
                    requirement names out loud: a control that is pretty and
                    misses is worse than one that is plain and lands.
                  */}
                  {FRAMEWORK_PICKER_VISIBLE && (
                    <div role="radiogroup" aria-label="Frontend framework" className="flex gap-1">
                      {FRAMEWORK_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          role="radio"
                          aria-checked={framework === option.value}
                          title={option.hint}
                          disabled={submitting}
                          onClick={() => setFramework(option.value)}
                          className={`h-11 cursor-pointer rounded-lg px-3 font-deck-mono text-[11.5px] font-medium tracking-[0.08em] uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                            framework === option.value
                              ? "bg-deck-blue-soft text-deck-blue-hi"
                              : "text-deck-text-faint hover:text-deck-text-soft"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  {/*
                    The counter, which used to sit at the end of the choices on
                    the left and now sits here.

                    It moved for two reasons. It was the fourth item in a row of
                    controls while being the only one that is not a control, so
                    it read as a disabled button; and it belongs next to the
                    thing it constrains — a limit is information about
                    submitting, and this is the submitting corner.

                    It replaced the &#8984;&#9166; hint rather than joining it. That hint
                    was already hidden on touch, which is most of this
                    audience, and at 11px beside a filled button nobody at a
                    booth was reading it — the shortcut still works for anyone
                    who would have guessed it.
                  */}
                  <span className="font-deck-mono text-[11.5px] tabular-nums text-deck-text-faint">
                    {prompt.length}/{MAX_PROMPT_LENGTH}
                  </span>
                  {/*
                    Disabled only while a submission is in flight, never for an
                    empty box. A greyed-out call to action is the first thing a
                    visitor would see, and the empty case is already caught by
                    the guard in `submit`.
                  */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="h-11 cursor-pointer rounded-lg bg-deck-blue px-6 text-[15px] font-semibold text-white transition-colors hover:bg-deck-blue-hi disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? "Starting…" : "Build it"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <p role="alert" className="mt-4 text-center text-sm font-medium text-deck-halt">
              {error}
            </p>
          )}
        </form>

        <section className="mt-7 flex w-full flex-col items-center gap-3" aria-labelledby="starters">
          <h2
            id="starters"
            className="font-deck-mono text-[11px] font-medium tracking-[0.18em] text-deck-text-faint uppercase"
          >
            Or start from one of these
          </h2>
          <div className="flex flex-wrap justify-center gap-2">
            {starters.map((item) => (
              <button
                key={item.title}
                type="button"
                onClick={() => choose(item.prompt)}
                disabled={submitting}
                title={item.prompt}
                className="min-h-11 cursor-pointer rounded-lg border border-deck-line bg-deck-raise/70 px-3.5 text-[13.5px] font-medium text-deck-text-soft transition-colors hover:border-deck-blue hover:text-deck-text disabled:cursor-not-allowed disabled:opacity-50"
              >
                {item.title}
              </button>
            ))}
          </div>
        </section>

        {/*
          Two lines, two voices. The claims are the skin's mono micro-label
          treatment, which is where that face earns its keep — three short
          phrases, read as a badge rather than as a sentence. The expiry note is
          an actual sentence and was set in the same face, where it wrapped to
          two wide mono lines and read like console output. It is Inter now.
        */}
        <div className="mt-8 flex flex-col items-center gap-1.5 text-center">
          {/* Tracked mono is wide, and at 11px this line wrapped on a 390px
              phone with "DEPLOYED" alone on the second row — which turns a badge
              into a sentence that ran out of room. A point smaller and a hair
              tighter below `sm` keeps it on one line where it belongs. */}
          <p className="font-deck-mono text-[10px] leading-[1.7] tracking-[0.08em] text-deck-text-faint uppercase sm:text-[11px] sm:tracking-[0.1em]">
            Real Postgres &middot; Real endpoints &middot; Really deployed
          </p>
          <p className="max-w-[420px] text-[12.5px] leading-[1.6] text-pretty text-deck-text-faint">
            Builds run on ephemeral Xano infrastructure and expire after about an hour.
          </p>
        </div>
      </main>
    </XanoScreen>
  );
}

/**
 * The six the row shows, by title.
 *
 * Titles rather than copies of the prompts: the text lives in
 * `lib/promptGallery.ts` and is edited there for every skin. A rename upstream
 * turns into a failing test here rather than a chip that quietly vanishes.
 *
 * Six rather than the gallery's fifteen for the same reason the hatch skin
 * shows six — a landing screen is an invitation, not a catalogue — but without
 * that skin's harder constraint, since there is no scenery here for a second
 * row to collide with.
 *
 * The six span all five groups and lead with the two that update live, because
 * this is the skin a visitor meets at the booth: the first thing worth showing
 * is the thing a static site generator cannot do.
 */
export const STARTERS = [
  "Appointment booking",
  "KPI dashboard",
  "Support chat",
  "Kanban board",
  "Inventory tracker",
  "Changelog",
] as const;

/**
 * The six, resolved against the shared gallery.
 *
 * A title that no longer exists is dropped rather than thrown on. Throwing
 * happens during render, and with no error boundary above it that turns a
 * one-word edit to a prompt title — a content change, in shared code, made by
 * someone with no reason to open this file — into a blank landing page nobody
 * can start a build from. Five chips is a worse row; no chips is an outage.
 *
 * The loud failure still exists, somewhere it costs nothing: the test file
 * asserts every title resolves, so a rename turns red in CI instead.
 */
function resolveStarters(): Array<{ title: string; prompt: string }> {
  const all = PROMPT_GALLERY.flatMap((group) => group.prompts);
  return STARTERS.map((title) => all.find((p) => p.title === title)).filter(
    (p): p is { title: string; prompt: string } => p !== undefined,
  );
}

/**
 * The four corner marks around the prompt box.
 *
 * Drawn as corners rather than as a border because the design's frame is open
 * at the edges: it reads as a viewfinder aimed at the box, and a closed
 * rectangle reads as the box itself. Purely decorative, so it is hidden from
 * the accessibility tree and takes no pointer events — the field behind it is
 * the target.
 */
function Brackets() {
  const corner = "absolute size-4 border-deck-blue/70";
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute -inset-2 animate-[deckFadeIn_0.5s_ease-out_0.15s_both]"
    >
      <span className={`${corner} top-0 left-0 border-t border-l`} />
      <span className={`${corner} top-0 right-0 border-t border-r`} />
      <span className={`${corner} bottom-0 left-0 border-b border-l`} />
      <span className={`${corner} right-0 bottom-0 border-r border-b`} />
    </div>
  );
}
